import React, { useState } from 'react';
import { useQuery } from 'react-query';
import {
  PhotoIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import mediaApi from '../api/mediaApi';

const ImagePicker = ({ 
  isOpen, 
  onClose, 
  onSelect, 
  selectedImage = null,
  multiple = false,
  category = null
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(category || 'all');
  const [tempSelected, setTempSelected] = useState(multiple ? [] : selectedImage);

  // Fetch media files
  const { data: mediaData, isLoading } = useQuery({
    queryKey: ['media-picker', currentPage, selectedCategory, searchQuery],
    queryFn: () => mediaApi.getAllMedia({
      page: currentPage,
      limit: 20,
      category: selectedCategory,
      search: searchQuery
    }),
    enabled: isOpen
  });

  // Fetch categories
  const { data: categoriesData } = useQuery({
    queryKey: ['media-categories'],
    queryFn: () => mediaApi.getCategories(),
    enabled: isOpen
  });

  const handleSelect = (file) => {
    if (multiple) {
      const isSelected = tempSelected.some(item => item.id === file.id);
      if (isSelected) {
        setTempSelected(tempSelected.filter(item => item.id !== file.id));
      } else {
        setTempSelected([...tempSelected, file]);
      }
    } else {
      setTempSelected(file);
    }
  };

  const handleConfirm = () => {
    onSelect(tempSelected);
    onClose();
  };

  const isSelected = (file) => {
    if (multiple) {
      return tempSelected.some(item => item.id === file.id);
    }
    return tempSelected?.id === file.id;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-5 border max-w-6xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {multiple ? 'Select Images' : 'Select Image'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="all">All Categories</option>
              {categoriesData?.categories?.map(category => (
                <option key={category} value={category}>
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Selected Items Summary */}
        {multiple && tempSelected.length > 0 && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
            <span className="text-sm text-blue-800">
              {tempSelected.length} image(s) selected
            </span>
          </div>
        )}

        {/* Media Grid */}
        <div className="max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm shadow rounded-md text-white bg-blue-500">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading...
              </div>
            </div>
          ) : mediaData?.files?.length === 0 ? (
            <div className="text-center py-12">
              <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No images found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Upload some images to get started.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
              {mediaData?.files?.map((file) => (
                <div
                  key={file.id}
                  className={`relative cursor-pointer group bg-white border-2 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-all ${
                    isSelected(file)
                      ? 'border-blue-500 ring-2 ring-blue-200'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => handleSelect(file)}
                >
                  {/* Selection indicator */}
                  {isSelected(file) && (
                    <div className="absolute top-2 right-2 z-10 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                      <CheckIcon className="h-4 w-4 text-white" />
                    </div>
                  )}
                  
                  <div className="aspect-square bg-gray-100 flex items-center justify-center overflow-hidden">
                    {file.mimeType.startsWith('image/') ? (
                      <img
                        src={mediaApi.getFileUrl(file.filename, true)}
                        alt={file.alt || file.originalName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-gray-400">
                        <PhotoIcon className="h-8 w-8" />
                      </div>
                    )}
                  </div>
                  
                  <div className="p-2">
                    <h3 className="text-xs font-medium text-gray-900 truncate" title={file.originalName}>
                      {file.originalName}
                    </h3>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {mediaData?.pagination && mediaData.pagination.totalPages > 1 && (
          <div className="flex items-center justify-center mt-6 space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 py-2 text-sm text-gray-700">
              Page {currentPage} of {mediaData.pagination.totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(Math.min(mediaData.pagination.totalPages, currentPage + 1))}
              disabled={currentPage === mediaData.pagination.totalPages}
              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={multiple ? tempSelected.length === 0 : !tempSelected}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {multiple 
              ? `Select ${tempSelected.length} Image${tempSelected.length !== 1 ? 's' : ''}`
              : 'Select Image'
            }
          </button>
        </div>
      </div>
    </div>
  );
};

// Reusable Image Input Component
export const ImageInput = ({ 
  label, 
  value, 
  onChange, 
  placeholder = "Select an image",
  category = null,
  required = false,
  error = null
}) => {
  const [showPicker, setShowPicker] = useState(false);

  const handleImageSelect = (selectedFile) => {
    if (selectedFile && onChange) {
      onChange(mediaApi.getFileUrl(selectedFile.filename));
    }
  };

  const handleClear = () => {
    if (onChange) {
      onChange('');
    }
  };

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      
      <div className="flex space-x-2">
        <div className="flex-1">
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={`block w-full rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
              error ? 'border-red-300' : 'border-gray-300'
            }`}
          />
        </div>
        
        <button
          type="button"
          onClick={() => setShowPicker(true)}
          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PhotoIcon className="h-4 w-4 mr-1" />
          Browse
        </button>
        
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Image Preview */}
      {value && (
        <div className="mt-2">
          <img
            src={value}
            alt="Preview"
            className="h-20 w-20 object-cover rounded-lg border border-gray-300"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}

      <ImagePicker
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        onSelect={handleImageSelect}
        category={category}
      />
    </div>
  );
};

export default ImagePicker;